// Shape fill and stroke rendering for node cache canvas

import useShapeUtils from '@/modules/Node/utils/shapeUtils';
import type { ShapeNode } from '@/modules/Node';

const { addShapeToContext } = useShapeUtils();

interface ShapeRenderState extends ShapeNode {
  xCache: number | null;
  yCache: number | null;
  surface: boolean;
  bgColor: string;
  shadow: boolean;
  shadowColor: string;
  shadowX: number;
  shadowY: number;
  shadowBlur: number;
  globalAlpha: number;
  borderColor: string;
  borderStyle: string;
}

export function drawShape(
  ctx: CanvasRenderingContext2D,
  state: ShapeRenderState,
  pixelRatio: number,
  zoomMax: number,
): Path2D {
  ctx.save();
  ctx.beginPath();

  const path = new Path2D();
  const zoomLevel = pixelRatio * zoomMax;

  addShapeToContext({
    ctx: path,
    node: state,
    center: { x: state.xCache!, y: state.yCache! },
  });

  const bgColor = state.surface ? state.bgColor : 'rgba(0, 0, 0, 0)';
  ctx.fillStyle = bgColor;

  if (state.shadow) {
    ctx.shadowColor = state.shadowColor;
    ctx.shadowOffsetX = state.shadowX * zoomLevel;
    ctx.shadowOffsetY = state.shadowY * zoomLevel;
    ctx.shadowBlur = state.shadowBlur * zoomLevel * 2;
  }

  ctx.globalAlpha = state.globalAlpha;
  ctx.fill(path);

  ctx.closePath();
  ctx.restore();

  return path;
}

export function drawStroke(ctx: CanvasRenderingContext2D, state: ShapeRenderState): void {
  if (!state.border || !state.borderWidth) return;
  ctx.beginPath();
  ctx.save();

  addShapeToContext({
    ctx,
    node: state,
    center: { x: state.xCache!, y: state.yCache! },
    border: true,
  });

  ctx.lineWidth = state.borderWidth;
  setBorderStyle(ctx, state.borderStyle, state.borderWidth);

  ctx.strokeStyle = state.borderColor;
  ctx.globalAlpha = state.globalAlpha;

  ctx.stroke();

  ctx.restore();
  ctx.closePath();
}

function setBorderStyle(ctx: CanvasRenderingContext2D, borderStyle: string, borderWidth: number): void {
  if (borderStyle === 'dashed') {
    ctx.setLineDash([borderWidth * 4, borderWidth * 2]);
  } else if (borderStyle === 'dotted') {
    ctx.lineCap = 'round';
    const multiplier = borderWidth === 1 ? 2 : 1;
    ctx.lineWidth = borderWidth + 1;
    ctx.setLineDash([borderWidth / 100, borderWidth * multiplier * 3]);
  }
}

export type { ShapeRenderState };
